import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import type { ApiError } from '@/types/api'

// ---------------------------------------------------------------------------
// Token refresh queue — serialises concurrent 401 retries
// ---------------------------------------------------------------------------

let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string | null) => void
  reject: (error: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  failedQueue = []
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isIpRestrictionError(data: unknown): boolean {
  if (data && typeof data === 'object' && 'error' in data) {
    const err = (data as ApiError).error
    return err.code === 'ip_restricted' || err.code === 'ip_not_allowed'
  }
  return false
}

// ---------------------------------------------------------------------------
// Store accessors — lazily resolved to avoid circular imports
// ---------------------------------------------------------------------------

type AuthStoreAccessor = () => {
  getAccessToken: () => string | null
  refreshToken: () => Promise<void>  // Uses HttpOnly cookie — no args needed
  logout: () => Promise<void>
}

type UiStoreAccessor = () => {
  showError: (message: string) => void
  showWarning: (message: string) => void
}

let _getAuthStore: AuthStoreAccessor | null = null
let _getUiStore: UiStoreAccessor | null = null

/**
 * Register the auth store accessor. Called once during app initialisation
 * (after Pinia is installed) to break the circular dependency between
 * the HTTP client and the stores.
 */
export function registerAuthStore(accessor: AuthStoreAccessor): void {
  _getAuthStore = accessor
}

/**
 * Register the UI store accessor for toast notifications.
 */
export function registerUiStore(accessor: UiStoreAccessor): void {
  _getUiStore = accessor
}

function getAccessToken(): string | null {
  try {
    return _getAuthStore?.().getAccessToken() ?? null
  } catch {
    return null
  }
}

async function refreshTokenSilently(): Promise<void> {
  if (!_getAuthStore) throw new Error('Auth store not registered')
  await _getAuthStore().refreshToken()
}

async function forceLogout(): Promise<void> {
  try {
    await _getAuthStore?.().logout()
  } catch {
    // best effort
  }
  // Redirect to login regardless
  if (typeof window !== 'undefined') {
    window.location.href = '/admin/login'
  }
}

function showErrorToast(message: string): void {
  try {
    _getUiStore?.().showError(message)
  } catch {
    // swallow — UI store may not be ready
  }
}

function showWarningToast(message: string): void {
  try {
    _getUiStore?.().showWarning(message)
  } catch {
    // swallow — UI store may not be ready
  }
}

// ---------------------------------------------------------------------------
// Exported for testing — allows resetting internal state between test runs
// ---------------------------------------------------------------------------

export function _resetRefreshState(): void {
  isRefreshing = false
  failedQueue = []
}

// ---------------------------------------------------------------------------
// Setup
// ---------------------------------------------------------------------------

export function setupInterceptors(client: AxiosInstance): void {
  // ---- Request interceptor --------------------------------------------------
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // CSRF protection header — always present
      config.headers.set('X-Requested-With', 'XMLHttpRequest')

      // Attach admin JWT from in-memory storage
      const token = getAccessToken()
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`)
      }

      return config
    },
    (error: unknown) => Promise.reject(error),
  )

  // ---- Response interceptor -------------------------------------------------
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

      // SECURITY: NEVER log response bodies — only status codes and error codes
      if (error.response) {
        const status = error.response.status
        const errorCode = error.response.data?.error?.code ?? 'unknown'

        // 401 — attempt silent refresh (but NOT for the refresh endpoint itself)
        const isRefreshRequest = originalRequest.url?.includes('/auth/refresh')
        if (status === 401 && !originalRequest._retry && !isRefreshRequest) {
          if (isRefreshing) {
            // Queue this request until the in-flight refresh completes
            return new Promise((resolve, reject) => {
              failedQueue.push({
                resolve: (token: string | null) => {
                  if (token) {
                    originalRequest.headers.set('Authorization', `Bearer ${token}`)
                  }
                  resolve(client(originalRequest))
                },
                reject,
              })
            })
          }

          originalRequest._retry = true
          isRefreshing = true

          try {
            await refreshTokenSilently()
            const newToken = getAccessToken()
            processQueue(null, newToken)

            // Retry the original request with the new token
            if (newToken) {
              originalRequest.headers.set('Authorization', `Bearer ${newToken}`)
            }
            return client(originalRequest)
          } catch (refreshError) {
            processQueue(refreshError, null)
            await forceLogout()
            return Promise.reject(refreshError)
          } finally {
            isRefreshing = false
          }
        }

        // 403 — IP restriction or insufficient permissions
        if (status === 403) {
          if (isIpRestrictionError(error.response.data)) {
            showErrorToast('Access denied from this IP address')
          } else {
            showErrorToast('Insufficient permissions')
          }
        }

        // 429 — rate limit
        if (status === 429) {
          const retryAfter = error.response.headers['retry-after']
          const waitMsg = retryAfter ? ` Please wait ${retryAfter} seconds.` : ''
          showWarningToast(`Rate limit exceeded.${waitMsg}`)
        }

        // Log only status + code — NEVER the body
        if (import.meta.env.DEV) {
          console.warn(`[admin-api] ${status} — ${errorCode}`)
        }
      }

      return Promise.reject(error)
    },
  )
}
